import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader, StatusPill } from "@/components/common";
import { Trophy, ArrowUpDown, ArrowLeft, Check, Star } from "lucide-react";

const SORT_KEYS = {
  score: { label: "Weighted score", get: (q) => q.score, dir: -1 },
  unit_price: { label: "Unit price", get: (q) => q.unit_price, dir: 1 },
  total_price: { label: "Total", get: (q) => q.total_price, dir: 1 },
  lead_time_days: { label: "Lead time", get: (q) => q.lead_time_days, dir: 1 },
  validity_days: { label: "Validity", get: (q) => q.validity_days, dir: -1 },
  supplier_rating: { label: "Rating", get: (q) => q.supplier_rating, dir: -1 },
};

export default function QuoteCompare() {
  const { id } = useParams();
  const nav = useNavigate();
  const [rfq, setRfq] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [shortlist, setShortlist] = useState(() => new Set());
  const [sortKey, setSortKey] = useState("score");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get(`/rfqs/${id}`);
      setRfq(r.data);
      const q = await api.get(`/quotes?rfq_id=${id}`);
      setQuotes(q.data);
    } catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
  };
  useEffect(() => { load(); }, [id]);

  const toggle = (qid) => setShortlist(s => {
    const n = new Set(s); n.has(qid) ? n.delete(qid) : n.add(qid); return n;
  });

  const award = async (qid) => {
    if (!window.confirm("Award this quote? Supplier identity will be revealed and an order will be created.")) return;
    setBusy(true);
    try { await api.post(`/rfqs/${id}/award`, { quote_id: qid }); nav("/app/orders"); }
    catch (e) { setErr(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };

  const sorted = useMemo(() => {
    if (!quotes.length) return [];
    const cfg = SORT_KEYS[sortKey];
    return [...quotes].sort((a, b) => (cfg.get(a) - cfg.get(b)) * cfg.dir);
  }, [quotes, sortKey]);

  const filtered = useMemo(() => {
    if (shortlist.size === 0) return sorted;
    return sorted.filter(q => shortlist.has(q.id));
  }, [sorted, shortlist]);

  if (!rfq) return <div style={{ color: "var(--ink-mute)" }}>{err || "Loading…"}</div>;
  const stats = computeStats(quotes);

  return (
    <>
      <button onClick={() => nav(`/app/rfqs/${id}`)} className="btn btn-ghost" data-testid="back-to-rfq" style={{ marginBottom: 14, fontSize: 12 }}>
        <ArrowLeft size={14} /> Back to RFQ
      </button>
      <PageHeader
        title={`Compare quotes · ${rfq.product_name}`}
        subtitle={<span className="font-mono" style={{ color: "var(--amber-400)" }}>{rfq.code} · {quotes.length} quote{quotes.length === 1 ? "" : "s"} · supplier identity revealed only on award</span>}
        actions={<StatusPill status={rfq.status} />}
      />

      {/* Summary tiles */}
      {quotes.length > 0 && (
        <div className="qc-summary">
          <Tile label="Best price"  value={`$${stats.minPrice.toFixed(2)}`} sub={`vs avg $${stats.avgPrice.toFixed(2)}`} />
          <Tile label="Fastest"     value={`${stats.minLead}d`} sub={`vs avg ${Math.round(stats.avgLead)}d`} />
          <Tile label="Top score"   value={stats.maxScore.toFixed(1)} sub="weighted /100" accent />
          <Tile label="Spread"      value={`$${(stats.maxPrice - stats.minPrice).toFixed(2)}`} sub={`min → max`} />
        </div>
      )}

      {/* Toolbar */}
      <div className="qc-toolbar">
        <div className="qc-sort-row">
          <span className="font-mono" style={{ fontSize: 11, color: "var(--ink-mute)", marginRight: 8 }}>SORT BY</span>
          {Object.entries(SORT_KEYS).map(([k, v]) => (
            <button key={k} onClick={() => setSortKey(k)} data-testid={`sort-${k}`} className={`btn ${sortKey === k ? "btn-primary" : "btn-ghost"}`} style={{ padding: "5px 12px", fontSize: 12 }}>
              <ArrowUpDown size={11} /> {v.label}
            </button>
          ))}
        </div>
        {shortlist.size > 0 && (
          <div className="qc-shortlist-active" data-testid="shortlist-active">
            Showing {shortlist.size} shortlisted · <button onClick={() => setShortlist(new Set())} style={{ color: "var(--amber-400)", background: "transparent", border: 0, cursor: "pointer" }}>Clear</button>
          </div>
        )}
      </div>

      {quotes.length === 0 ? (
        <div className="card" style={{ padding: 32, textAlign: "center", color: "var(--ink-mute)" }}>
          No quotes yet — suppliers are being notified.
        </div>
      ) : (
        <div className="qc-grid" data-testid="quote-compare-grid">
          {filtered.map((q, i) => (
            <article key={q.id} className={`qc-card ${i === 0 && sortKey === "score" ? "is-top" : ""}`} data-testid={`quote-card-${q.id}`}>
              {i === 0 && sortKey === "score" && <div className="qc-top-pill"><Trophy size={11} /> TOP RANKED</div>}
              <header className="qc-card-head">
                <div className="font-mono qc-supplier">{q.supplier_code}</div>
                <button data-testid={`shortlist-${q.id}`} onClick={() => toggle(q.id)}
                        className="qc-star" aria-pressed={shortlist.has(q.id)}>
                  <Star size={16} fill={shortlist.has(q.id) ? "var(--amber-500)" : "transparent"} color="var(--amber-500)" />
                </button>
              </header>

              <div className="qc-price">
                <div className="qc-unit">${q.unit_price.toFixed(2)}<span className="qc-unit-sub">/unit</span></div>
                <div className="qc-total">${q.total_price.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})} <span style={{ color: "var(--ink-mute)", fontSize: 11 }}>total</span></div>
              </div>

              <div className="qc-row"><span>Lead time</span><b>{q.lead_time_days}d</b></div>
              <div className="qc-row"><span>Validity</span><b>{q.validity_days}d</b></div>
              <div className="qc-row"><span>Origin</span><b>{q.origin || "—"}</b></div>
              <div className="qc-row"><span>Shipping</span><b style={{ fontSize: 12 }}>{q.shipping_terms || "—"}</b></div>
              <div className="qc-row"><span>Rating</span><b>{q.supplier_rating?.toFixed(1)} ★</b></div>

              {q.score_breakdown && (
                <div className="qc-breakdown">
                  <div className="qc-bd-head font-mono">SCORE BREAKDOWN</div>
                  <Bar label="Price"     val={q.score_breakdown.price}     max={50} />
                  <Bar label="Lead time" val={q.score_breakdown.lead_time} max={30} />
                  <Bar label="Rating"    val={q.score_breakdown.rating}    max={20} />
                  <div className="qc-bd-total">
                    <span>Total</span><b className="font-mono" style={{ color: "var(--amber-400)" }}>{q.score?.toFixed(1)} / 100</b>
                  </div>
                </div>
              )}

              {q.remarks && <div className="qc-remarks">"{q.remarks}"</div>}

              <footer className="qc-card-foot">
                {rfq.status === "awarded" ? (
                  <StatusPill status={q.status} />
                ) : (
                  <button data-testid={`award-${q.id}`} disabled={busy} onClick={() => award(q.id)} className="btn btn-primary" style={{ width: "100%", justifyContent: "center" }}>
                    <Check size={14} /> Award
                  </button>
                )}
              </footer>
            </article>
          ))}
        </div>
      )}
    </>
  );
}

function Tile({ label, value, sub, accent }) {
  return (
    <div className="qc-tile">
      <div className="qc-tile-label font-mono">{label}</div>
      <div className="qc-tile-value" style={{ color: accent ? "var(--amber-400)" : "var(--ink)" }}>{value}</div>
      <div className="qc-tile-sub">{sub}</div>
    </div>
  );
}

function Bar({ label, val, max }) {
  const pct = Math.max(0, Math.min(100, (val / max) * 100));
  return (
    <div className="qc-bar">
      <div className="qc-bar-label">{label}</div>
      <div className="qc-bar-track"><div className="qc-bar-fill" style={{ width: `${pct}%` }} /></div>
      <div className="qc-bar-val font-mono">{val.toFixed(1)}<span style={{ color: "var(--ink-mute)" }}>/{max}</span></div>
    </div>
  );
}

function computeStats(quotes) {
  const prices = quotes.map(q => q.unit_price);
  const leads  = quotes.map(q => q.lead_time_days);
  const scores = quotes.map(q => q.score || 0);
  return {
    minPrice: Math.min(...prices), maxPrice: Math.max(...prices),
    avgPrice: prices.reduce((a, b) => a + b, 0) / prices.length,
    minLead:  Math.min(...leads),
    avgLead:  leads.reduce((a, b) => a + b, 0) / leads.length,
    maxScore: Math.max(...scores),
  };
}
